import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import { ScoreBar } from '@/components/ui/ScoreBar';
import { CategoryIndicators } from './CategoryIndicators';
import type { Product } from '../completeness.api';
import { ProductImage } from './ProductImage';

interface ProductRowProps {
  product: Product;
  clientId?: string;
}

export function ProductRow({ product, clientId }: ProductRowProps) {
  const { t } = useTranslation();
  const { role } = useEffectiveAccess();
  // De terugknop van productdetail gaat naar de lijst van herkomst, waar de lijststand weer staat (story 9).
  const { pathname } = useLocation();
  const productPath = clientId
    ? `/data-quality/${clientId}/products/${product.id}`
    : `/products/${product.id}`;
  // FR14a: only the canonical customer product list opens the media page from the
  // thumbnail. `role` (not `clientId`) decides this: `clientId` also resolves truthy
  // for a customer's own GLN, so it cannot distinguish the two contexts here.
  const opensMediaPage = role === 'customer';
  const thumbnail = <ProductImage gln={clientId ?? ''} gtin={product.gtin} productName={product.name} variant="thumbnail" />;

  return (
    <tr
      data-testid={`product-row-${product.id}`}
      className="transition-all duration-100"
      style={{ cursor: 'pointer' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
          'var(--primary-faint)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = '';
      }}
    >
      <td className="px-4 py-3 text-[0.8125rem] font-medium" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <div className="flex items-center gap-3">
          {opensMediaPage
            ? (
              <Link
                aria-label={t('products.media.openMediaPage', { productName: product.name })}
                state={{ brand: product.brand }}
                to={`/products/${product.id}/media`}
              >
                {thumbnail}
              </Link>
            )
            : thumbnail}
          <div className="flex flex-col">
            <Link
              state={{ terugNaar: pathname }}
              to={productPath}
              style={{ color: 'var(--secondary)' }}
            >
              {product.name}
            </Link>
            <span
              data-testid="product-gtin"
              className="text-[0.6875rem]"
              style={{ color: 'var(--foreground-muted)' }}
            >
              {product.gtin}
            </span>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-[0.8125rem]" style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}>
        {product.brand}
      </td>
      <td className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <CategoryIndicators categories={product.categoryScores} />
      </td>
      <td className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-soft)' }}>
        <ScoreBar className="max-w-[140px]" score={product.score} variant="compact" />
      </td>
      <td
        className="px-4 py-3 text-center text-[0.8125rem] font-medium"
        data-testid="product-missing-fields"
        aria-label={
          typeof product.missingRequiredFieldCount === 'number'
            ? (t('products.missingFieldsCount', { count: product.missingRequiredFieldCount }) as string)
            : undefined
        }
        style={{ borderBottom: '1px solid var(--border-soft)', color: 'var(--foreground-muted)' }}
      >
        {typeof product.missingRequiredFieldCount === 'number' ? product.missingRequiredFieldCount : t('common.notAvailable')}
      </td>
    </tr>
  );
}
